/**
 * Meta (`META_ADS`) attribute visibility, shared by the product detail page and
 * the quick-view dialog so both stay in sync.
 *
 * Compared against a normalized field OR label, so `retailer_id`, `Retailer Id`
 * and `RetailerId` all collapse to the same key and match.
 */

/** Identifiers Meta views drop entirely: Retailer Id repeats the header and the
 *  Image Link is already the gallery. */
const META_HIDDEN_ATTRIBUTES = new Set(['retailerid', 'imagelink']);

/** Merchant-owned values Lily passes through unchanged. Shown so operators can
 *  confirm what reaches Meta, but read-only: they are not optimized or edited here. */
const META_MERCHANT_READ_ONLY_ATTRIBUTES = new Set(['price', 'saleprice']);

/** Lowercased, alphanumerics-only form so field/label variants collapse to one key. */
function normalizeAttr(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** True when the field or its label normalizes to a key in `keys`. */
function matches(keys: Set<string>, attr: { field: string; label?: string | null }): boolean {
  if (keys.has(normalizeAttr(attr.field))) return true;
  return attr.label != null && keys.has(normalizeAttr(attr.label));
}

/** True when this optimization is an identifier the UI hides on a `META_ADS` batch. */
export function isMetaHiddenAttribute(attr: { field: string; label?: string | null }): boolean {
  return matches(META_HIDDEN_ATTRIBUTES, attr);
}

/** Applies the Meta view rules to a batch's optimizations: drops the hidden
 *  identifiers and marks the merchant price / sale price rows read-only. */
export function toMetaVisibleOptimizations<
  T extends { field: string; label?: string | null; readOnly?: boolean },
>(optimizations: T[]): T[] {
  return optimizations
    .filter((opt) => !isMetaHiddenAttribute(opt))
    .map((opt) =>
      matches(META_MERCHANT_READ_ONLY_ATTRIBUTES, opt) ? { ...opt, readOnly: true } : opt,
    );
}
